import React from 'react'
import {
  makeStyles,
  createStyles,
  TextField,
  InputLabel,
  CircularProgress,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined'
// import { ReactComponent as Google } from '../../assets/icons/google.svg'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { omit } from 'lodash'
import { DIALING_CODES } from 'src/utils/constant'
import { signIn, getUserInfo } from 'src/store/slices/authSlice'
import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'
import TagManager from 'react-gtm-module'
import FocusError from 'src/mobile/components/FocusError'
import AuthHeader from 'src/mobile/components/Auth/AuthHeader'
import AuthTabs from 'src/mobile/components/Auth/AuthTabs'
import DialingCodeSelector from 'src/mobile/components/DialingCodeSelector'
import { loginOrSignEvent } from 'src/utils/trackingCode'
// import useRedirectAfterLogin from 'src/hooks/useRedirectAfterLogin'
import { toast } from 'react-toastify'
import { Helmet  } from 'react-helmet'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      minHeight: `calc(100vh - ${theme.spacing(41)}px)`,
    },
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    paddingForm: {
      backgroundColor: '#fff',
      padding: theme.spacing(2),
      paddingTop: 0,
    },
    formLayout: {
      padding: theme.spacing(0, 2.5),
    },
    continueTip: {
      fontWeight: ' 600 !important',
      fontSize: '26px',
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(5),
    },
    border: {
      borderBottom: '1px solid',
      borderBottomColor: theme.palette.primary.light,
      width: '100%',
    },
    content: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      color: theme.palette.primary.light,
    },

    inputLabel: {
      fontWeight: 400,
      color: '#111827',
      fontSize: 14,
      marginTop: 5,
    },
    helperText: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
    },
    submit: {
      height: 56,
      fontSize: 18,
      fontWeight: 600,
      [theme.breakpoints.down('xs')]: {
        fontSize: 16,
      },
    },
  }),
)

const initialValues = {
  verificationType: 'phone',
  email: '',
  password: '',
  dialingCode: DIALING_CODES[0].value,
  phone: '',
  icon: false,
}

const SignIn = ({ source }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  // const redirectAfterLogin = useRedirectAfterLogin()
  const { signInStatus, userInfoStatus } = useSelector((state) => state.auth)
  const schema = oriSchema(t).pick(['verificationType', 'email', 'dialingCode', 'phone', 'password'])

  // const [showPassword, setShowPassword] = useState(false)
  const location = useLocation()
  const { pathname } = location
  const history = useHistory()

  return (
    <Box width="100%" maxWidth={590} margin="0 auto" className={classes.root}>
      <Helmet>
        <meta name="description" content={t('seoDescription.signin')} />
        <title>{t('seoTitle.signin')}</title>
      </Helmet>     
      {source === 'order' ? (
        ''
      ) : (
        <AuthHeader title={isMobile ? t('common.user_login') : t('tips.welcome_back')} />
      )}
      <Box className={source === 'order' || !isMobile ? {} : classes.formLayout}>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={async (values) => {
            if (signInStatus === 'pending' || userInfoStatus === 'pending') return
            const omitKeys =
              values.verificationType === 'email'
                ? ['verificationType', 'phone', 'dialingCode']
                : ['verificationType', 'email']
            const pureValues = omit(values, omitKeys)
            await dispatch(signIn(pureValues)).unwrap()
            await dispatch(getUserInfo()).unwrap()
            toast.success(t('common.signin_success'))
            TagManager.dataLayer({
              dataLayer: {
                event: 'signinComplete',
              },
            })
            // tracking code
            const method =  values.verificationType === 'phone' ? 'phone' : 'email'
            loginOrSignEvent('login', method)
            history.push(
              pathname === `/${i18n.language}/add-order/signin` ? `/${i18n.language}/add-order` : '/',
            )

            // return redirectAfterLogin({
            //   redirectUrl:
            //     pathname === `/${i18n.language}/add-order/signin` ? `/${i18n.language}/add-order` : '/',
            // })
          }}
        >
          {(props) => {
            const { values, handleSubmit, setFieldValue, handleChange, errors, touched, setFieldTouched } =
              props
            const isError = (field) => touched[field] && Boolean(errors[field])
            const errorText = (field) => touched[field] && errors[field]

            useTranslateFormErrors(errors, touched, setFieldTouched)

            const handleTabsChange = (value) => value && setFieldValue('verificationType', value)
            const handleClickShowPassword = () => setFieldValue('icon', !values.icon)
            return (
              <form
                className={source === 'order' ? classes.paddingForm : classes.form}
                onSubmit={handleSubmit}
              >
                {source === 'order' && !isMobile && (
                  <Typography variant="h3" className={classes.continueTip}>
                    {t('common.signin_continue')}
                  </Typography>
                )}
                <AuthTabs
                  onChange={handleTabsChange}
                  authType={t('common.sign_in')}
                  hideLogo={source === 'order' ? true : false}
                />
                {source === 'order' && isMobile && (
                  <Typography variant="h3" className={classes.continueTip}>
                    {t('common.signin_continue')}
                  </Typography>
                )}
                {values.verificationType === 'phone' ? (
                  <>
                    <InputLabel classes={{ root: classes.inputLabel }}>{t('form.mobile_phone')}</InputLabel>
                    <Box my={1} mt={1}>
                      <TextField
                        fullWidth
                        id="phone"
                        name="phone"
                        variant="outlined"
                        value={values.phone}
                        onChange={handleChange}
                        error={isError('phone')}
                        helperText={errorText('phone')}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.helperText,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <DialingCodeSelector
                              value={values.dialingCode}
                              onChange={(dialingCode) => setFieldValue('dialingCode', dialingCode)}
                            />
                          ),
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <InputLabel classes={{ root: classes.inputLabel }}>
                      {t('common.email_address')}
                    </InputLabel>
                    <Box my={1}>
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        variant="outlined"
                        autoComplete="true"
                        value={values.email}
                        onChange={handleChange}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.helperText,
                          },
                        }}
                      />
                    </Box>
                  </>
                )}
                <InputLabel classes={{ root: classes.inputLabel }}>{t('form.password')}</InputLabel>
                <TextField
                  margin="normal"
                  fullWidth
                  name="password"
                  type={values.icon ? 'text' : 'password'}
                  id="password"
                  variant="outlined"
                  value={values.password}
                  onChange={handleChange}
                  autoComplete="true"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  FormHelperTextProps={{
                    classes: {
                      root: classes.helperText,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {!values.icon ? (
                            <VisibilityOffOutlined color="primary" />
                          ) : (
                            <VisibilityOutlined color="primary" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Link
                  to={
                    source === 'order'
                      ? `/${i18n.language}/add-order/forgot-password`
                      : `/${i18n.language}/forgot-password`
                  }
                >
                  <Box color="text.primary" component="span" borderBottom="1px solid">
                    {t('are_you_sure.forget_your_password')}
                  </Box>
                </Link>

                <Box mt={3}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="secondary"
                    className={classes.submit}
                  >
                    {signInStatus === 'pending' || userInfoStatus === 'pending' ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      t('common.sign_in')
                    )}
                  </Button>
                  {/* <Box my={3} className={classes.container}>
                  <Box className={classes.border} />
                  <Box component="span" className={classes.content}>
                    或
                  </Box>
                  <Box className={classes.border} />
                </Box>
                <Button fullWidth variant="outlined" startIcon={<Google />}>
                  以Google帳戶登入
                </Button> */}
                  <Box mt={2} fontSize="body2.fontSize" justifyContent="center" display="flex">
                    <Link
                      to={
                        source === 'order' ? `/${i18n.language}/add-order/signup` : `/${i18n.language}/signup`
                      }
                      replace
                    >
                      <Box color="text.primary" component="span">
                        {t('are_you_sure.do_not_have_an_account')}
                        <Box component="span" borderBottom="1px solid">
                          {t('common.sign_up_now')}
                        </Box>
                      </Box>
                    </Link>
                  </Box>
                </Box>
                <FocusError />
              </form>
            )
          }}
        </Formik>
      </Box>
    </Box>
  )
}

export default SignIn
